import React, { createContext, useContext, useCallback, useMemo } from 'react';
import { useAuth } from '../hooks/useAuth.jsx';
import { useData } from '../hooks/useData.jsx';
import { useSync } from '../hooks/useSync.jsx';
import { DriveAPI } from '../core/DriveAPI.js';
import { getCurrentCyclePeriod, buildCyclePerformanceReport } from '../core/dateHelpers.js';

const AppContext = createContext(null);

export function AppProvider({ children }) {
    // ── Auth Hook ───────────────────────────────────────────
    const { authUser, authChecked, authRestoring, handleSignedIn, handleSignOut, acquireDriveToken } = useAuth();

    // ── Data Hook ───────────────────────────────────────────
    const data = useData();

    // ── Sync Hook ───────────────────────────────────────────
    const sync = useSync(
        authUser,
        acquireDriveToken,
        data.applyData,
        data.getAllData,
        false // isRunningAsPWA - will be computed in consuming component
    );

    // ── Utility: Spend entry handler ──────────────────────────
    const handleAddSpend = useCallback((setter, getter, itemId, amount, note) => {
        const today = new Date().toISOString().split('T')[0];
        setter(getter.map(item => {
            if (item.id !== itemId) return item;
            const entry = { date: today, amount, note };
            return {
                ...item,
                actualSpent: (item.actualSpent || 0) + amount,
                spendLog: [...(item.spendLog || []), entry],
            };
        }));
    }, []);

    // ── Budget Cycle helpers ─────────────────────────────────
    const currentCycle = useMemo(() => getCurrentCyclePeriod(), []);

    // ── Memoized context value ────────────────────────────────
    const value = useMemo(() => ({
        // Auth
        authUser,
        authChecked,
        authRestoring,
        handleSignedIn,
        handleSignOut,
        acquireDriveToken,

        // Data (spread all data hook return values)
        ...data,

        // Sync (spread all sync hook return values)
        ...sync,

        // Sync function aliases for backward compatibility
        syncNow: sync.pullFromDrive,
        createBackup: sync.backupToDrive,
        forceReconnect: sync.handleForceReconnect,

        // Utilities
        handleAddSpend,
        currentCycle,
        DriveAPI,
        buildCyclePerformanceReport,
    }), [
        authUser,
        authChecked,
        authRestoring,
        handleSignedIn,
        handleSignOut,
        acquireDriveToken,
        data,
        sync,
        handleAddSpend,
        currentCycle,
    ]);

    return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export function useAppContext() {
    const context = useContext(AppContext);
    if (!context) {
        throw new Error('useAppContext must be used within an AppProvider');
    }
    return context;
}

export { AppContext };
